import React, { useState } from "react";
import { Box, Typography, Grid, ButtonGroup, Button } from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import DateRangePicker from "../../components/DateRangePicker";
import EmailPerformancePerServer from "./components/EmailPerformancePerServer";
import SmsPerformancePerServer from "./components/SmsPerformancePerServer";
import EmailPerformancePerAccount from "./components/EmailPerformancePerAccount";
import SmsPerformancePerAccount from "./components/SmsPerformancePerAccount";
import EmailCostByServer from "./components/EmailCostByServer";
import EmailCostByDomain from "./components/EmailCostByDomain";
import SmsCostByServer from "./components/SmsCostByServer";
import SmsCostByNumber from "./components/SmsCostByNumber";
import dayjs from "dayjs";

export default function SuperAdminDashboard() {
  const [startDate, setStartDate] = useState(dayjs().subtract(7, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [selectedRange, setSelectedRange] = useState("7_days");
  const [customDateRange, setCustomDateRange] = useState(null);

  const handleDateRangeApply = () => {
    // Set custom date range and clear selectedRange
    setCustomDateRange({
      start: startDate,
      end: endDate,
    });
    setSelectedRange("custom");
  };

  const dateRanges = [
    { label: "Last 7 Days", value: "7_days", format: "day" },
    { label: "Last 30 Days", value: "30_days", format: "day" },
    { label: "Last Year", value: "year", format: "month" },
  ];

  // Helper to get format for a range value
  const getFormatForRange = (rangeValue) => {
    const range = dateRanges.find((r) => r.value === rangeValue);
    return range?.format || "day";
  };

  const handleRangeChange = (range) => {
    setSelectedRange(range);
    setCustomDateRange(null); // Clear custom range when selecting preset

    const now = dayjs();
    let newStartDate, newEndDate;

    switch (range) {
      case "7_days":
        newStartDate = now.clone().subtract(7, "day");
        newEndDate = now;
        break;
      case "30_days":
        newStartDate = now.clone().subtract(30, "day");
        newEndDate = now;
        break;
      case "year":
        newStartDate = now.clone().subtract(1, "year");
        newEndDate = now;
        break;
      default:
        newStartDate = now.clone().subtract(7, "day");
        newEndDate = now;
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <AdminPanelSettingsIcon
          sx={{ mr: 2, fontSize: 32, color: "primary.main" }}
        />
        <Typography component="h1" variant="h4">
          Super Admin Dashboard
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: 2, sm: 0 },
          mb: 2,
        }}
      >
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onApply={handleDateRangeApply}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", sm: "flex-end" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <ButtonGroup size="small" variant="outlined">
            {dateRanges.map((range) => (
              <Button
                key={range.value}
                variant={
                  selectedRange === range.value ? "contained" : "outlined"
                }
                onClick={() => handleRangeChange(range.value)}
              >
                {range.label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <EmailPerformancePerServer
            startDate={customDateRange?.start || startDate}
            endDate={customDateRange?.end || endDate}
            selectedRange={selectedRange}
            format={
              selectedRange === "custom"
                ? "day"
                : getFormatForRange(selectedRange)
            }
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <SmsPerformancePerServer
            startDate={customDateRange?.start || startDate}
            endDate={customDateRange?.end || endDate}
            selectedRange={selectedRange}
            format={
              selectedRange === "custom"
                ? "day"
                : getFormatForRange(selectedRange)
            }
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <EmailPerformancePerAccount
            startDate={customDateRange?.start || startDate}
            endDate={customDateRange?.end || endDate}
            selectedRange={selectedRange}
            format={
              selectedRange === "custom"
                ? "day"
                : getFormatForRange(selectedRange)
            }
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <SmsPerformancePerAccount
            startDate={customDateRange?.start || startDate}
            endDate={customDateRange?.end || endDate}
            selectedRange={selectedRange}
            format={
              selectedRange === "custom"
                ? "day"
                : getFormatForRange(selectedRange)
            }
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <EmailCostByServer
            startDate={customDateRange?.start || startDate}
            endDate={customDateRange?.end || endDate}
            selectedRange={selectedRange}
            format={
              selectedRange === "custom"
                ? "day"
                : getFormatForRange(selectedRange)
            }
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <SmsCostByServer
            startDate={customDateRange?.start || startDate}
            endDate={customDateRange?.end || endDate}
            selectedRange={selectedRange}
            format={
              selectedRange === "custom"
                ? "day"
                : getFormatForRange(selectedRange)
            }
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <EmailCostByDomain
            startDate={customDateRange?.start || startDate}
            endDate={customDateRange?.end || endDate}
            selectedRange={selectedRange}
            format={
              selectedRange === "custom"
                ? "day"
                : getFormatForRange(selectedRange)
            }
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <SmsCostByNumber
            startDate={customDateRange?.start || startDate}
            endDate={customDateRange?.end || endDate}
            selectedRange={selectedRange}
            format={
              selectedRange === "custom"
                ? "day"
                : getFormatForRange(selectedRange)
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
}
